/**
 * Phase F.1b / GEN-2998 — flag-ON family section renderer.
 *
 * Pure presentational wrapper that takes the per-row plan from
 * `OrderInProgressFamilyDisplay` plus the comparator items from
 * `projectComparatorsToOrderInProgressRows()` and renders the family
 * section. Guardian rows are delegated to a caller-provided
 * `renderGuardianRow` callback so this component never depends on the
 * legacy `<GuardianContactCard>` props bag — keeping the new code
 * loosely coupled and trivially unit-testable.
 *
 * Stateless. No autosave, no modals, no Redux dispatch. The caller wires
 * `onEdit` per row to the existing legacy modal openers in F.1b; F.1b-ii
 * will replace those with `<ComparatorModal>`.
 */

import type { ReactNode } from 'react'

import { ComparatorCard } from '@/components/order/ComparatorCard'
import type { OrderInProgressFamilyDisplay } from '@/ordering/orderInProgressFamilyCards'
import type { ComparatorListParentItem } from '@/ordering/comparatorsToOrderInProgressRows'

export interface ComparatorListProps {
  /**
   * Display rows from `deriveOrderInProgressFamilyRows`. The list is
   * iterated in document order so guardian / parent positions match the
   * legacy render exactly.
   */
  display: OrderInProgressFamilyDisplay
  /**
   * Prepared parent items from
   * `projectComparatorsToOrderInProgressRows`. Items are matched to
   * BIOLOGICAL_MOTHER / BIOLOGICAL_FATHER rows by `rowKind`.
   */
  parentItems: ReadonlyArray<ComparatorListParentItem>
  /**
   * Caller-provided guardian renderer. Returning `null` skips the row
   * (matches the legacy `<GuardianContactCard>` mounting rules).
   */
  renderGuardianRow: (
    row: Extract<OrderInProgressFamilyDisplay['rows'][number], { kind: 'GUARDIAN_CONTACT' }>,
    index: number
  ) => ReactNode
  /**
   * Caller-provided per-parent edit handler factory. Returning
   * `undefined` for a row makes the card non-editable (matches the
   * legacy `canEditIndications` / `canEditComparatorParents`
   * gating). Receives the resolved `editTarget` so the caller can wire
   * to the right legacy modal opener.
   */
  resolveEditHandlerForParent: (item: ComparatorListParentItem) => (() => void) | undefined
}

export function ComparatorList({
  display,
  parentItems,
  renderGuardianRow,
  resolveEditHandlerForParent,
}: ComparatorListProps) {
  return (
    <>
      {display.rows.map((row, rowIndex) => {
        if (row.kind === 'GUARDIAN_CONTACT') {
          return (
            <ComparatorListGuardianSlot key={`comparator-list-guardian-${rowIndex}`}>
              {renderGuardianRow(row, rowIndex)}
            </ComparatorListGuardianSlot>
          )
        }
        const item = parentItems.find((i) => i.rowKind === row.kind)
        if (!item) {
          // Defensive: projection should have signalled `fallbackToLegacy`
          // upstream — but guard the render path so a stale projection
          // can never blank the family section.
          return null
        }
        return (
          <ComparatorCard
            key={`comparator-list-${item.editTarget}-${rowIndex}`}
            comparator={item.comparator}
            onEdit={resolveEditHandlerForParent(item)}
            needsAttention={item.needsAttention}
            statusMessage={item.statusMessage}
            statusVariant={item.statusVariant}
          />
        )
      })}
    </>
  )
}

/**
 * Tiny wrapper around the guardian render slot. Keeps a stable element
 * identity so React reconciliation doesn't churn when guardian props
 * change in place. Pure pass-through — no extra DOM.
 */
function ComparatorListGuardianSlot({ children }: { children: ReactNode }) {
  return <>{children}</>
}
